import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import ValidationInfo from '../../../../validation/validation-info';
import { TranslationService } from '../../../../services/translation.service';
import TextFieldFormGroup from '../../../Shared/TextFieldFormGroup';
import type IValidatable from '../../../Shared/validatable.interface';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import TransportWarrantyActualTripSelectorSummary from './components/TransportWarrantyActualTripSelectorSummary';
import ReplacementTransport from './components/ReplacementTransport';
import { ReplacementTransportMode } from './components/ReplacementTransport/replacement-transport-mode.enum';
import { TransportWarrantyActualArrivalSelectorField, TransportWarrantyActualArrivalSelectorMode } from './transport-warranty-actual-arrival-selector.enum';
import FileUpload from '../../../Shared/FileUpload';
import { SanitizerService } from '../../../../services/sanitizer.service';
import IFile from '../../../Shared/FileUpload/models/file.model';

export interface ITransportWarrantyActualArrivalSelectorStateValue {
  mode: TransportWarrantyActualArrivalSelectorMode;
  transportMode: ReplacementTransportMode;
  from: string;
  to: string;
  taxiSum: number | undefined;
  registrationNumber: string;
  distance: number | undefined;
  parkingFee: number | undefined;
  congestionTax: number | undefined;
  receiptSum: number | undefined;
  otherTransportType: string;
  comments: string;
  files: IFile[];
}

export interface ITransportWarrantyActualArrivalSelectorState {
  value: ITransportWarrantyActualArrivalSelectorStateValue;
  validationInfo: ValidationInfo;
}

interface ITransportWarrantyActualArrivalSelectorProps {
  className?: string;
  state: ITransportWarrantyActualArrivalSelectorState;
  onChange?: ((value: ITransportWarrantyActualArrivalSelectorStateValue) => void) | undefined;
  onKeyUp?: ((field: TransportWarrantyActualArrivalSelectorField) => void) | undefined;
  onEditClick?: () => void;
}

export default forwardRef<IValidatable, ITransportWarrantyActualArrivalSelectorProps>(function TransportWarrantyActualArrivalSelector(
  { className, state, onChange, onKeyUp, onEditClick }: ITransportWarrantyActualArrivalSelectorProps,
  ref
) {
  // === STATE ===
  const [transportMode, setTransportMode] = useState<ReplacementTransportMode>(state.value.transportMode);

  // === REFERENCES ===
  const commentsRef = useRef<IValidatable>(null!);
  const replacementTransportRef = useRef<IValidatable>(null!);
  const fileUploadRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const replacementTransportValidationInfo = replacementTransportRef.current.checkValidity();
      const fileUploadValidationInfo = fileUploadRef.current?.checkValidity() ?? new ValidationInfo();
      return new ValidationInfo('', [], {
        comments: commentsRef.current.checkValidity(),
        ...replacementTransportValidationInfo.children,
        fileUploadValidationInfo,
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      const replacementTransportValidationInfo = replacementTransportRef.current.handleServerValidationErrors(errors);
      const uploadFilesValidationInfo = fileUploadRef.current?.handleServerValidationErrors(errors) ?? new ValidationInfo();

      return new ValidationInfo('', [], {
        comments: commentsRef.current.handleServerValidationErrors(errors),
        ...replacementTransportValidationInfo.children,
        uploadFilesValidationInfo,
      });
    },
  }));

  function handleOnReplacementTransportChange(value: ITransportWarrantyActualArrivalSelectorStateValue) {
    setTransportMode(value.transportMode);
    onChange?.(value);
  }

  return (
    <div className={`actual-arrival-selector ${className ?? ''}`}>
      <div className={state.value.mode === TransportWarrantyActualArrivalSelectorMode.edit ? '' : 'd-none'}>
        <TextFieldFormGroup
          ref={commentsRef}
          autocomplete="off"
          inputMode="text"
          required={true}
          renderAsTextArea={true}
          maxLength={2000}
          labelText={TranslationService.get('delaycompensatiotransportwarrantyformpage.comments')}
          rows={5}
          inputId={'actual-arrival-selector-comments'}
          serverValidationName="actualArrivalComments"
          state={{
            value: state.value.comments,
            validationInfo: state.validationInfo.children.comments ?? new ValidationInfo(),
          }}
          onChange={(value) => {
            onChange?.({ ...state.value, comments: value });
          }}
          onKeyUp={() => {
            onKeyUp?.(TransportWarrantyActualArrivalSelectorField.comments);
          }}
        />
        <ReplacementTransport ref={replacementTransportRef} state={state} onChange={handleOnReplacementTransportChange} onKeyUp={onKeyUp} />
        <hr />
        {transportMode !== ReplacementTransportMode.none && (
          <>
            <FileUpload
              id="transport-warranty-file-upload"
              ref={fileUploadRef}
              isRequired={false}
              legendText={TranslationService.get('delaycompensatiotransportwarrantyformpage.uploadfiles')}
              state={{
                value: state.value.files,
                validationInfo: state.validationInfo.children.files ?? new ValidationInfo(),
              }}
              onFilesAdded={(files) => {
                onChange?.({ ...state.value, files: [...state.value.files, ...files] });
              }}
              onFileRemoved={(file) => {
                onChange?.({ ...state.value, files: state.value.files.filter((f) => f !== file) });
              }}
            />
            <p>{TranslationService.get('delaycompensatiotransportwarrantyformpage.receiptinfo')}</p>
            <div
              className="bg-gray p-3 last-child-margin-bottom-none"
              dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('delaycompensatiotransportwarrantyformpage.addressdata')) }}
            />
          </>
        )}
      </div>
      <div className={state.value.mode === TransportWarrantyActualArrivalSelectorMode.summary ? '' : 'd-none'}>
        <TransportWarrantyActualTripSelectorSummary stateValue={state.value} onEditClick={onEditClick} />
      </div>
    </div>
  );
});
